import { Component, HostListener, OnInit, ViewEncapsulation } from '@angular/core';
import { BlogsService } from './blogs.service';
import { Blog } from '../shared/models/Blog';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Editor, Toolbar } from 'ngx-editor';
import { BlogContent } from '../shared/models/BlogContent';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BlogImage } from '../shared/models/BlogImage';
import { Observable } from 'rxjs';
import { ComponentCanDeactivate } from '../shared/pending-changes.guard';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'app-blogs',
    templateUrl: './blogs.component.html',
    styleUrl: './blogs.component.scss',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class BlogsComponent implements OnInit, ComponentCanDeactivate {
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return !this.pendingChanges;
  }

  public blogs: Blog[] = [];

  public editors: Editor[] = [];

  public contentEditor: Editor;
  public headerEditor: Editor;

  public createBlog: boolean = true;
  public selectedBlog: Blog;
  public blogContent: string;
  public blogHeader: string;
  public blogHeaderImage: string;

  public pendingChanges = false;

  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['link'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
    ['format_clear'],
    ['indent', 'outdent'],
    ['undo', 'redo']
  ]

  public languages = [
    { id: "de", name: "German" },
    { id: "it", name: "Italian" },
    { id: "en", name: "English" }
  ]

  public blogFilters = [
    { id: 0, name: "All" },
    { id: 1, name: "Draft" },
    { id: 2, name: "Published" }
  ]

  public selectedLanguage = this.languages[0];

  public selectedFilter = this.blogFilters[0];

  public availableImages: BlogImage[] = [];

  constructor(private _blogsService: BlogsService, private _toastr: ToastrService, private _translate: TranslateService, private _modalService: NgbModal) {}

  ngOnInit(): void {
    this.selectedBlog = new Blog();
    this.selectedBlog.name = "";
    this.headerEditor = new Editor();
    this.contentEditor = new Editor();

    this.blogContent = "";
    this.blogHeader = "";
    this.blogHeaderImage = "";
    this.pendingChanges = false;

    this.getBlogs();
  }

  /**
   * Loads all blogs into an array
   */

  getBlogs(blogId?: number) {
    this._blogsService.getBlogs().subscribe(res => {
      this.blogs = res.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      if (this.blogs.length === 0) {
        this.createBlog = true;
      } else {
        if (blogId) {
          this.switchBlog(this.blogs.find(b => b.id === blogId));
        } else {
          this.switchBlog(this.blogs[0]);
        }
      }
    });
  }

  saveBlog(sendMessage: boolean) {
    if (this.selectedBlog.name === "" || this.selectedBlog.status === undefined) {
      return;
    }
    this._blogsService.saveBlog(this.selectedBlog).subscribe(res => {
      this.pendingChanges = false;
      if (sendMessage) {
        this._toastr.success("Blog saved successfully");
      }
    },
    error => {
      this._toastr.error("There was an error saving the blog");
    });
  }

  onSaveClick() {
    this.saveContent();
    this.saveBlog(true);
  }

  saveContent() {
    if (this.selectedBlog.contents.find(bc => bc.language === this.selectedLanguage.id)) {
      this.selectedBlog.contents.find(bc => bc.language === this.selectedLanguage.id).content = this.blogContent;
      this.selectedBlog.contents.find(bc => bc.language === this.selectedLanguage.id).header = this.blogHeader;
      this.selectedBlog.contents.find(bc => bc.language === this.selectedLanguage.id).headerImage = this.blogHeaderImage;
    } else {
      const blogContent = new BlogContent();
      blogContent.language = this.selectedLanguage.id;
      blogContent.content = this.blogContent;
      blogContent.header = this.blogHeader;
      blogContent.headerImage = this.blogHeaderImage;
      this.selectedBlog.contents.push(blogContent);
    }
  }

  insertBlog() {
    if (this.selectedBlog.name !== "") {
      const blog = new Blog();
      blog.name = this.selectedBlog.name;
      blog.status = 0;
      this._blogsService.saveBlog(blog).subscribe(res => {
        this.getBlogs(res.id);
      });
    }
  }

  uploadImage(event: any) {
    const files = event.target.files;
    if (files.length === 0) { return; }
    if (files.length > 1) { return; }
    const mimeType = files[0].type;
    if (mimeType.match(/image\/*/) == null) {
      return this._toastr.warning(this._translate.instant('event.advertiseEvent.imageError'));
    }

    const imageFileNameArray = files[0].name.split('.');
    const imageNameFinale = imageFileNameArray[imageFileNameArray.length - 1].toLocaleLowerCase();
    const imageName = Math.random().toString(36).slice(-8) + '.' + imageNameFinale;

    const blob = new Blob([files[0]], {type: mimeType});
    const formData = new FormData();
    formData.append('blogId', this.selectedBlog.id.toString());
    formData.append('imageName', imageName);
    formData.append('file', blob, imageName);

    this._blogsService.uploadBlogImage(formData).subscribe(res => {
      this._toastr.success("Image uploaded successfully");
      this.selectedBlog.images.push(res);
    });
  }

  switchBlog(blog: Blog, save?: boolean) {
    if (save && !this.createBlog) {
      this.saveContent();
      this.saveBlog(false);
    }
    this.selectedBlog = blog;
    this.createBlog = false;
    this.blogContent = blog.contents.find(c => c.language === this.selectedLanguage.id) ? blog.contents.find(c => c.language === this.selectedLanguage.id).content : "";
    this.blogHeader = blog.contents.find(c => c.language === this.selectedLanguage.id) ? blog.contents.find(c => c.language === this.selectedLanguage.id).header : "";
    this.blogHeaderImage = blog.contents.find(c => c.language === this.selectedLanguage.id) ? blog.contents.find(c => c.language === this.selectedLanguage.id).headerImage : "";
    this.pendingChanges = false;
    this.availableImages = blog.images;
  }

  onCreateBlog() {
    if (this.blogs.length > 0) {
      this.saveContent();
      this.saveBlog(false);
    }
    this.selectedBlog = new Blog();
    this.createBlog = true;
    this.selectedBlog.name = "";
    this.blogContent = "";
    this.blogHeader = "";
    this.blogHeaderImage = "";
    this.pendingChanges = false;
    this.availableImages = [];
    this.selectedLanguage = this.languages.find(l => l.id === "de");
  }

  publishBlog() {
    this._blogsService.publishBlog(this.selectedBlog.id.toString()).subscribe(res => {
      this._toastr.success(this.selectedBlog.status === 1 ? "Blog unpublished successfully" : "Blog published successfully");
      this.getBlogs(this.selectedBlog.id);
    });
  }

  onLanguageChange(event) {
    this.saveContent();

    this.selectedLanguage = event;

    if (this.selectedBlog.contents.find(bc => bc.language === event.id)) {
      this.blogContent = this.selectedBlog.contents.find(bc => bc.language === event.id).content;
      this.blogHeader = this.selectedBlog.contents.find(bc => bc.language === event.id).header;
      this.blogHeaderImage = this.selectedBlog.contents.find(bc => bc.language === event.id).headerImage;
    } else {
      this.blogContent = "";
      this.blogHeader = "";
      this.blogHeaderImage = "";
    }

    this.saveBlog(false);
  }

  onImageSelect(image: BlogImage) {
    this.blogContent += '<img src="' + image.imageUrl + '">';
    this.saveContent();
    this.saveBlog(false);
  }

  setImageAsHeader(image: BlogImage) {
    this.blogHeaderImage = image.imageUrl;
    this.saveContent();
    this.saveBlog(false);
  }

  onFilterChange(event) {
    this.selectedFilter = event.id;
  }

  getFilteredBlogs() {
    switch (this.selectedFilter.id) {
      case 0:
        return this.blogs;
      case 1:
        return this.blogs.filter(b => b.status === 0);
      case 2:
        return this.blogs.filter(b => b.status === 1);
      default:
        return this.blogs;
    }
  }

  onContentChange() {
    this.pendingChanges = true;
  }

  onDeleteButtonClick(modal) {
    this._modalService.open(modal);
  }

  onDeleteConfirm() {
    this._blogsService.deleteBlog(this.selectedBlog.id.toString()).subscribe(res => {
      this._modalService.dismissAll();
      this._toastr.success("Blog deleted successfully");
      this.getBlogs();
    });
  }

}
